/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';

import { Theme as MaterialTheme, useTheme, Stack, TextField, Button, Dialog, DialogTitle, Typography, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, Backdrop, CircularProgress, FormHelperText } from "@mui/material";
import Grid from '@mui/material/Unstable_Grid2';
import { useState, useEffect, useContext, ChangeEvent } from "react";

import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../layouts/AuthorizedLayout';
import { toast } from 'react-toastify';
import ActorBusinessLogic from '../../../businessLogic/ActorBusinessLogic';
import { useParams } from 'react-router';
import { IActorGuiModel, IActorPostGuiModel, IActorPutGuiModel } from '../../../model/IActorGuiModel';
import { NumericFormatWhole } from '../../../common/CustomFunctions';
import { digiAccActorType } from '../../../common/TextTypes';
import { ICountryGuiModel } from '../../../model/ICountryGuiModel';
import CountryBusinessLogic from '../../../businessLogic/CountryBusinessLogic';
interface IDialogProps {
    open: boolean,
    closeDialog: (reason?: "backdropClick" | "escapeKeyDown") => void,
    actorType: digiAccActorType | undefined,
    actor: IActorGuiModel | undefined,
}
interface IAddActorModel {
    actorType: digiAccActorType | null,
    name: string | null,
    address1: string | null,
    address2: string | null,
    country: string | null,
    postalCode: string | null,
    postArea: string | null,
    email: string | null,
    businessNumber: string | null,
    bankAccount: string | null,
    comment: string | null,
}

interface IAddActorErrorModel {
    actorType: boolean,
    name: boolean,
    address1: boolean,
    address2: boolean,
    country: boolean,
    postalCode: boolean,
    postArea: boolean,
    email: boolean,
    businessNumber: boolean,
    bankAccount: boolean,
    comment: boolean,
}

const DialogActorAddEdit = (props: IDialogProps): JSX.Element => {

    const theme = useTheme();

    const { t, i18n } = useTranslation();

    const { organizationId } = useParams();

    const [loader, setLoader] = useState(false);
    const [countryLoader, setCountryLoader] = useState(false);
    const [countriesError, setCountriesError] = useState(false);

    const [countries, setCountries] = useState<ICountryGuiModel[]>([]);

    useEffect(() => {

        let isMounted = true; //Avoid memory leaks. Async function running after dismount.
        const logic = new CountryBusinessLogic();

        const getCountries = async () => {
            setCountryLoader(true);
            const result = await logic.getCountries(organizationId ? organizationId : "");
            setCountryLoader(false);

            if (isMounted) {
                if (result.hasErrors) {
                    setCountries([]);
                    setCountriesError(true);
                    toast(t("basic.errorCountry"));
                } else {
                    setCountries(result.value);
                    setCountriesError(false);
                }
            }
        }

        getCountries();

        return () => { isMounted = false };

    }, [organizationId])

    const [error, setError] = useState<IAddActorErrorModel>({
        actorType: false,
        name: false,
        address1: false,
        address2: false,
        country: false,
        postalCode: false,
        postArea: false,
        email: false,
        businessNumber: false,
        bankAccount: false,
        comment: false,
    });

    const postActor = async (fieldsData: IAddActorModel) => {
        if (organizationId) {
            const logic = new ActorBusinessLogic();
            const data: IActorPostGuiModel = {
                name: fieldsData.name,
                bankAccount: fieldsData.bankAccount,
                businessNumber: fieldsData.businessNumber,
                addressLine1: fieldsData.address1,
                addressLine2: fieldsData.address2,
                isoCountryCode: fieldsData.country,
                postalCode: fieldsData.postalCode,
                postArea: fieldsData.postArea,
                emailAddress: fieldsData.email,
                comment: fieldsData.comment
            }

            if (props.actorType) {
                setLoader(true);
                const result = await logic.postActor(organizationId, props.actorType === "Supplier" ? "Supplier" : "Customer", data);
                setLoader(false);

                if (result.hasErrors) {
                    toast(t("basic.errorAddActor") + result.errors.join(", "));
                } else {
                    toast(t("basic.actorAdded"));
                    props.closeDialog();
                }
            } else if (fieldsData.actorType) {
                setLoader(true);
                const result = await logic.postActor(organizationId, fieldsData.actorType === "Supplier" ? "Supplier" : "Customer", data);
                setLoader(false);

                if (result.hasErrors) {
                    toast(t("basic.errorAddActor") + result.errors.join(", "));
                } else {
                    toast(t("basic.actorAdded"));
                    props.closeDialog();
                }
            } else {
                toast(t("basic.errorActorTypeIsRequired"));
                setError({
                    ...error,
                    actorType: true,
                })
            }

        } else {
            toast(t("basic.errorWithOrganization"));
        }
    }
    const putActor = async (fieldsData: IAddActorModel) => {
        if (organizationId) {
            if (props.actor) {
                const logic = new ActorBusinessLogic();

                const data: IActorPutGuiModel = {
                    name: fieldsData.name ? fieldsData.name ? { value: fieldsData.name } : { value: null } : undefined,
                    bankAccount: fieldsData.bankAccount ? fieldsData.bankAccount ? { value: fieldsData.bankAccount } : { value: null } : undefined,
                    businessNumber: fieldsData.businessNumber ? fieldsData.businessNumber ? { value: fieldsData.businessNumber } : { value: null } : undefined,
                    addressLine1: fieldsData.address1 ? fieldsData.address1 ? { value: fieldsData.address1 } : { value: null } : undefined,
                    addressLine2: fieldsData.address2 ? fieldsData.address2 ? { value: fieldsData.address2 } : { value: null } : undefined,
                    isoCountryCode: fieldsData.country ? fieldsData.country ? { value: fieldsData.country } : { value: null } : undefined,
                    postalCode: fieldsData.postalCode ? fieldsData.postalCode ? { value: fieldsData.postalCode } : { value: null } : undefined,
                    postArea: fieldsData.postArea ? fieldsData.postArea ? { value: fieldsData.postArea } : { value: null } : undefined,
                    emailAddress: fieldsData.email ? fieldsData.email ? { value: fieldsData.email } : { value: null } : undefined,
                    comment: fieldsData.comment ? fieldsData.comment ? { value: fieldsData.comment } : { value: null } : undefined,
                }

                if (props.actorType === "Supplier" && props.actor.supplierId) {
                    setLoader(true);
                    const result = await logic.putActor(
                        organizationId,
                        "Supplier",
                        props.actor.supplierId,
                        data);
                    setLoader(false);

                    if (result.hasErrors) {
                        toast(t("basic.errorActorModified") + result.errors.join(", "));
                    } else {
                        toast(t("basic.actorModified"));
                        props.closeDialog();
                    }


                } else if (props.actorType === "Customer" && props.actor.customerId) {
                    setLoader(true);
                    const result = await logic.putActor(
                        organizationId,
                        "Customer",
                        props.actor.customerId,
                        data);
                    setLoader(false);

                    if (result.hasErrors) {
                        toast(t("basic.errorActorModified") + result.errors.join(", "));
                    } else {
                        toast(t("basic.actorModified"));
                        props.closeDialog();
                    }

                } else {
                    toast(t("basic.errorActorIdMissing"));
                }
            } else {
                toast(t("basic.errorActorIdMissing"));
            }
        } else {
            toast(t("basic.errorWithOrganization"));
        }
    }

    const handleAddActorSubmit = (event: React.FormEvent<HTMLFormElement>) => {

        event.preventDefault();
        const data = new FormData(event.currentTarget);

        const fieldsData: IAddActorModel = {
            actorType: !props.actorType ? data.get("actorType") as digiAccActorType : props.actorType === "Customer" ? "Customer" : "Supplier",
            name: data.get("name") as string,
            address1: data.get("address1") as string,
            address2: data.get("address2") as string,
            country: data.get("country") as string,
            postalCode: data.get("postalCode") as string,
            postArea: data.get("postArea") as string,
            email: data.get("email") as string,
            businessNumber: data.get("businessNumber") as string,
            bankAccount: (data.get("bankAccount") as string).replaceAll(".",""),
            comment: data.get("comment") as string,
        }

        let errorStates = {
            actorType: props.actorType ? false : fieldsData.actorType ? false : true,
            name: !(fieldsData.name?.replaceAll(" ", "") !== ""),
            address1: !(fieldsData.address1?.replaceAll(" ", "") !== ""),
            address2: false,
            country: !(fieldsData.country?.replaceAll(" ", "") !== ""),
            postalCode: !(fieldsData.postalCode?.replaceAll(" ", "") !== ""),
            postArea: !(fieldsData.postArea?.replaceAll(" ", "") !== ""),
            email: false,
            businessNumber: false,
            bankAccount: false,
            comment: false,
        }

        setError(errorStates);

        if (countries.findIndex(e => e.isoTwoLetters === fieldsData.country) === -1) {
            toast(t("basic.errorCountryMissing"))
        } else {
            if (errorStates.name || errorStates.address1 || errorStates.postalCode || errorStates.postArea) {
                toast(t("basic.errorFieldMissing"));
            } else if (!props.actor) {
                postActor(fieldsData);
            } else {
                putActor(fieldsData);
            }
        }

    }

    //Remember to remove dots and stuff during API call with replace command.
    //function validateAccount(): boolean {
    //    if (actor.bankAccount && actor.bankAccount !== "" && !/^\d+(?:\.\d+)*$/.test(actor.bankAccount ? actor.bankAccount : "")) {
    //        return false;
    //    } else {
    //        return true;
    //    }
    //}

    //#######################################################################
    //#######################################################################
    //#######################################################################
    return <Dialog
        maxWidth="xs"
        fullWidth
        onClose={(event, reason) => props.closeDialog(reason)}
        open={props.open}
    >
        <form onSubmit={handleAddActorSubmit} noValidate>
            <DialogTitle>
                <Grid container justifyContent="space-between" alignItems="center">
                    <Grid>
                        <Typography variant="h6">
                            {props.actor ?
                                props.actorType === "Supplier" ? t("basic.modifySupplier") : t("basic.modifyCustomer") :
                                !props.actorType ? t("basic.addActor") :
                                    props.actorType === "Customer" ? t("basic.addCustomer") : t("basic.addSupplier")}
                        </Typography>
                    </Grid>
                    <Grid>
                        <Button variant="text" onClick={() => props.closeDialog()}>{t("basic.close")}</Button>
                    </Grid>
                </Grid>
            </DialogTitle>
            <DialogContent dividers sx={{ padding: 0 }}>
                <Stack direction="column" spacing={4} padding={theme.spacing(4)}>
                    <FormControl color="warning" size="small" fullWidth sx={{ display: props.actorType ? "none" : "grid" }}>
                        <InputLabel id="label-actorType">{t("basic.actorType")}</InputLabel>
                        <Select color="warning"
                            labelId="select-actorType-label"
                            id="select-actorType"
                            name="actorType"
                            label={t("basic.actorType")}
                            error={error.actorType}
                            onChange={() => setError({ ...error, actorType: false })} >
                            <MenuItem value={"Supplier"}>{t("basic.supplier")}</MenuItem>
                            <MenuItem value={"Customer"}>{t("basic.customer")}</MenuItem>
                        </Select>
                        <FormHelperText id="helpertext-actorType" sx={{ display: error.actorType ? "block" : "none" }}>{t("basic.errorActorTypeIsRequired")}</FormHelperText>
                    </FormControl>
                    <TextField
                        size="small"
                        variant="outlined"
                        autoComplete="off"
                        error={error.name}
                        onChange={() => setError({ ...error, name: false })}
                        defaultValue={props.actor ? props.actor.name : ""}
                        color="warning"
                        label={t("basic.name")}
                        helperText={error.name ? t("basic.errorNameIsRequired") : ""}
                        fullWidth
                        id="name"
                        name="name"
                        type="text" />
                    <TextField
                        size="small"
                        variant="outlined"
                        autoComplete="off"
                        error={error.email}
                        onChange={() => setError({ ...error, email: false })}
                        defaultValue={props.actor ? props.actor.emailAddress : ""}
                        color="warning"
                        label={t("basic.email")}
                        fullWidth
                        id="email"
                        name="email"
                        type="text" />
                    <TextField
                        size="small"
                        variant="outlined"
                        autoComplete="off"
                        error={error.address1}
                        onChange={() => setError({ ...error, address1: false })}
                        defaultValue={props.actor ? props.actor.addressLine1 : ""}
                        color="warning"
                        label={t("basic.address") + " 1"}
                        helperText={error.address1 ? t("basic.errorAddress1IsRequired") : ""}
                        fullWidth
                        id="address1"
                        name="address1"
                        type="text" />
                    <TextField
                        size="small"
                        variant="outlined"
                        autoComplete="off"
                        error={error.address2}
                        onChange={() => setError({ ...error, address2: false })}
                        defaultValue={props.actor ? props.actor.addressLine2 : ""}
                        color="warning"
                        label={t("basic.address") + " 2"}
                        fullWidth
                        id="address2"
                        name="address2"
                        type="text" />
                    <FormControl color="warning" size="small" fullWidth>
                        <InputLabel id="label-country">{t("basic.country")}</InputLabel>
                        <Select color="warning"
                            labelId="select-country-label"
                            id="select-country"
                            name="country"
                            label={t("basic.country")}
                            error={error.country}
                            onChange={() => setError({ ...error, country: false })}
                            defaultValue={props.actor ? props.actor.isoCountryCode : "NO"}
                        >
                            {countries.map(e =>
                                <MenuItem value={e.isoTwoLetters}>{e.nameEnglish}</MenuItem>)}
                        </Select>
                        <FormHelperText id="helpertext-country" sx={{ display: error.country ? "block" : "none" }}>{t("basic.errorCountryIsRequired")}</FormHelperText>
                    </FormControl>
                    <TextField
                        size="small"
                        variant="outlined"
                        autoComplete="off"
                        error={error.postalCode}
                        onChange={() => setError({ ...error, postalCode: false })}
                        defaultValue={props.actor ? props.actor.postalCode : ""}
                        color="warning"
                        label={t("basic.postalCode")}
                        helperText={error.postalCode ? t("basic.errorPostalCodeIsRequired") : ""}
                        fullWidth
                        id="postalCode"
                        name="postalCode" />
                    <TextField
                        size="small"
                        variant="outlined"
                        autoComplete="off"
                        error={error.postArea}
                        onChange={() => setError({ ...error, postArea: false })}
                        defaultValue={props.actor ? props.actor.postArea : ""}
                        color="warning"
                        label={t("basic.postArea")}
                        helperText={error.postArea ? t("basic.errorPostAreaIsRequired") : ""}
                        fullWidth
                        id="postArea"
                        name="postArea" />
                    <TextField
                        size="small"
                        variant="outlined"
                        autoComplete="off"
                        error={error.businessNumber}
                        onChange={() => setError({ ...error, businessNumber: false })}
                        defaultValue={props.actor ? props.actor.businessNumber ? props.actor.businessNumber.replaceAll(".", "") : "" : ""}
                        
                        color="warning"
                        label={t("basic.businessNumber")}
                        fullWidth
                        id="businessNumber"
                        name="businessNumber" />
                    <TextField
                        size="small"
                        variant="outlined"
                        autoComplete="off"
                        error={error.bankAccount}
                        onChange={() => setError({ ...error, bankAccount: false })}
                        defaultValue={props.actor ? props.actor.bankAccount ? props.actor.bankAccount.replaceAll(".","") : "" : ""}
                        InputProps={{ inputComponent: NumericFormatWhole as any }}
                        color="warning"
                        label={t("basic.bankAccount")}
                        fullWidth
                        id="bankAccount"
                        name="bankAccount" />
                    <TextField
                        size="small"
                        multiline
                        rows={3}
                        maxRows={3}
                        variant="outlined"
                        autoComplete="off"
                        error={error.comment}
                        onChange={() => setError({ ...error, comment: false })}
                        defaultValue={props.actor ? props.actor.comment : ""}
                        color="warning"
                        label={t("basic.comment")}
                        fullWidth
                        id="comment"
                        name="comment" />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" type="submit">{t("basic.save")}</Button>
            </DialogActions>
        </form>
        <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={loader}
        >
            <CircularProgress color="inherit" />
        </Backdrop>
    </Dialog>
}
export default DialogActorAddEdit;